import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  ApiService,
  AppointmentModel,
  Clinic,
  ClinicService,
  CrudService,
  Eligibility,
  Facility,
  FacilityService,
  filterAction,
  INSURANCE_PROVIDER_TRANSACTION_TYPE,
  InsuranceProvider,
  InsuranceProviderConfiguration,
  InsuranceProviderConfigurationModel,
  InsuranceProviderConfigurationService,
  InsuranceProviderLookup,
  InsuranceProviderLookupsService,
  maxDateValidator,
  oneOfValidator,
  Patient,
  PatientInsurance,
  PatientInsuranceType,
  PatientSexes,
  SelectOption,
  ServiceType,
  ServiceTypes,
  Sexes,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import Fuse from 'fuse.js'
import { flatten, get, isEmpty, uniq } from 'lodash'
import moment from 'moment-timezone'
import { BehaviorSubject, EMPTY, Observable, of, ReplaySubject, Subject } from 'rxjs'
import {
  catchError,
  combineLatestWith,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'

import { ProgressService } from '../progress'

const FALLBACK_SERVICE_TYPE_CODE = '30'
const NO_PAYER_CONFIGURED_ERROR = 'dialog.eligibility.no-payer-configured'

export interface PullEligibilityDialogInput {
  patient?: Patient
  appointment?: AppointmentModel
  insurances?: PatientInsurance[]
  insurance?: PatientInsurance
  insuranceType?: PatientInsuranceType
}

interface PullEligibilityRequest {
  patientId?: string
  patientInsuranceId?: string
  lookupId?: string
  providerId?: string
  insuranceType?: PatientInsuranceType
  appointmentId?: string
  facilityId?: string
  memberId?: string
  groupNumber?: string
  dob?: string
  sex?: string
  dateOfService?: string | 'unspecified'
  serviceTypeCodes?: string[]
  patientName?: { family?: string; given?: string }
}

/**
 * Narrows codes to the ones the payer lists. An empty or absent list means the
 * payer never told us which codes it takes, so none are filtered out.
 */
function filterSupportedCodes(
  codes: string[],
  payerServiceTypes?: ServiceType[],
): string[] {
  if (!payerServiceTypes?.length) {
    return codes
  }

  return codes.filter((code) =>
    payerServiceTypes.some((serviceType) => serviceType.code === code),
  )
}

@Component({
  selector: 'mmx-pull-eligibility-dialog',
  templateUrl: './pull-eligibility-dialog.component.html',
  standalone: false,
})
export class PullEligibilityDialogComponent
implements OnInit, AfterViewInit, OnDestroy
{
  get patient(): Patient | undefined {
    return this.data.patient
  }

  sexes = Sexes
  lookups: InsuranceProviderLookup[] = []
  providers: InsuranceProvider[] = []
  filteredLookups$: Observable<InsuranceProviderLookup[]>
  filteredProviders$: Observable<InsuranceProvider[]>
  errorMessage: string
  payerErrorMessage: string
  eligibility: Eligibility
  popularInsurances: InsuranceProviderLookup[]
  showLookupSelector = false
  showProviderSelector = false

  public serviceTypes = ServiceTypes
  public serviceFilterCtrl = new FormControl<string>(null)
  public filteredServiceTypes: ReplaySubject<ServiceType[]> = new ReplaySubject<
    ServiceType[]
  >(1)

  public carrierNotes: string | void
  public requiresMemberId: boolean
  public submit$ = new Subject<void>()
  public progress$ = this.progressService.progress$.pipe(
    combineLatestWith(
      this.insuranceProviderLookupsService.loading$,
      this.insuranceProviderConfigurationService.isLoading$,
    ),
    map(([local, lookups, config]) => local || lookups || config),
  )

  private onDestroy$ = new Subject<void>()
  private fuseLookups: Fuse<InsuranceProviderLookup>
  private fuseProviders: Fuse<InsuranceProvider>
  private fuseServiceTypes = new Fuse(this.serviceTypes, {
    keys: ['code', 'name'],
    shouldSort: true,
  })
  private clinic: Clinic
  private facility?: Facility
  private get defaultServiceTypeCodes(): string[] {
    const configured = this.clinic?.modulesPreferences?.defaultEligibilityServiceTypeCodes
    return configured?.length ? configured : [FALLBACK_SERVICE_TYPE_CODE]
  }

  private get tz(): string {
    return (
      this.facility?.timezone ||
      this.clinic?.settings.defaultTimezone ||
      'America/New_York'
    )
  }

  /**
   * Only set when the payer's Availity configuration declares a cap. Payers that
   * accept a single code are handled by the API fanning the request out, so the
   * dropdown does not restrict the clinician to one.
   */
  public maxRepeats?: number

  // private patient$ = new BehaviorSubject<Patient>(null)
  public insurances$ = new BehaviorSubject<PatientInsurance[]>(null)

  // public insurances$ = this.patient$.pipe(
  //   takeUntil(this.onDestroy$),
  //   filter(Boolean),
  //   switchMap(patient => this.crudService.index(PatientInsurance, { patientId: patient.id })),
  //   tap(insurances => this.insurances = insurances),
  //   shareReplay(1),
  // )

  private lookups$ = of(null).pipe(
    switchMap(() =>
      this.insuranceProviderLookupsService.connected$.pipe(
        takeUntil(this.onDestroy$),
      ),
    ),
    tap((lookups: InsuranceProviderLookup[]) => {
      this.lookups = lookups
      this.popularInsurances = this.lookups.filter((c) => c.popular)
      this.fuseLookups = new Fuse(this.lookups, {
        keys: ['viewValue'],
        shouldSort: true,
      })
    }),
  )

  ws$ = this.ws.clinicianMessage$.pipe(
    takeUntil(this.onDestroy$),
    filterAction(WebsocketAction.ELIGIBILITY_INQUIRY_CONSOLE),
    tap((response) => {
      this.progressService.stop('pulling')

      if (response?.data) {
        this.eligibility = new Eligibility(response.data)

        if (
          this.eligibility.data.errors &&
          this.eligibility.data.errors.length > 0
        ) {
          this.errorMessage = this.eligibility.data.errors
            .map((e) => e.message)
            .join(', ')
        } else {
          this.dialogRef.close(this.eligibility)
        }
      } else {
        const message =
          response?.message ??
          this.translateService.instant('dialog.eligibility.unknown-error')
        this.errorMessage = message
      }
    }),
    catchError((err) => {
      const message =
        get(err, 'error.errors[0].message') ||
        get(err, 'error.message') ||
        this.translateService.instant('dialog.eligibility.unknown-error')
      this.errorMessage = message
      return of(null)
    }),
  )

  constructor(
    private clinicService: ClinicService,
    public facilityService: FacilityService,
    private dialogRef: MatDialogRef<PullEligibilityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PullEligibilityDialogInput,
    private restService: ApiService,
    private ws: WebSocketService,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private crudService: CrudService,
    private insuranceProviderLookupsService: InsuranceProviderLookupsService,
    public insuranceProviderConfigurationService: InsuranceProviderConfigurationService,
    public progressService: ProgressService<
      'pulling' | 'providers' | 'lookup' | 'insurances'
    >,
  ) {
    this.data = this.data || {}
  }

  form = this.fb.group({
    lookup: this.fb.control<InsuranceProviderLookup>(null),
    provider: this.fb.control<InsuranceProvider>(null),
    insurance: this.fb.control<PatientInsurance>(null),
    facilityId: this.fb.control<string>(null),
    useDOB: this.fb.control<boolean>(false),
    memberId: this.fb.control<string>(null, Validators.required),
    groupNumber: this.fb.control<string>(null),
    patientName: this.fb.group({
      given: this.fb.control<string>(null, Validators.required),
      family: this.fb.control<string>(null, Validators.required),
    }),
    dob: this.fb.control<moment.Moment>(null, [
      maxDateValidator(moment()),
      Validators.required,
    ]),
    sex: this.fb.control<PatientSexes>(null),
    dateOfService: this.fb.control<moment.Moment>(null),
    serviceTypeCodes: this.fb.control<string | string[]>(
      null,
      this.updateServiceTypeCodesValidator(),
    ),
  })

  ngOnInit(): void {
    this.clinic = this.clinicService.clinic
    // this.facilityService.current$
    //   .pipe(takeUntil(this.onDestroy$))
    //   .subscribe((facility) => {
    //     this.facility = facility
    //   })

    this.filteredServiceTypes.next(this.serviceTypes.slice())
    this.ws$.subscribe()

    if (this.data.insurance) {
      this.form.patchValue({
        insurance: this.data.insurance,
        memberId: this.data.insurance.memberId,
        groupNumber: this.data.insurance.groupNumber,
      })
    }

    if (this.data.patient) {
      // this.patient$.next(this.data.patient)

      this.form.patchValue({
        patientName: {
          given: this.patient.name?.given,
          family: this.patient.name?.family,
        },
        dob: this.patient.dob,
        sex: this.patient.sex,
      })

      if (!this.patient.dob) {
        this.errorMessage = this.translateService.instant(
          'dialog.eligibility.patient-not-dob',
        )
      }
    }

    if (this.data.insurances) {
      this.insurances$.next(this.data.insurances)
    }

    // Prepopulate the facility: when opened from an appointment, use that
    // appointment's facility; otherwise fall back to the globally selected one.
    if (this.data.appointment?.locationId) {
      this.form.patchValue({ facilityId: this.data.appointment.locationId })
      // The facility is fixed by the appointment, so lock the selector.
      // getRequestObject() reads facilityId via the control's getRawValue() since
      // disabled controls are excluded from form.value.
      this.form.controls.facilityId.disable()
    } else {
      this.facilityService.current$
        .pipe(takeUntil(this.onDestroy$), filter(Boolean), take(1))
        .subscribe((facility) => {
          if (!this.form.value.facilityId) {
            this.form.patchValue({ facilityId: facility.id })
          }
        })
    }

    this.form.updateValueAndValidity()

    this.submit$
      .pipe(
        filter(() => this.form.valid && this.hasPayer),
        tap(() => {
          this.progressService.start('pulling')
          this.errorMessage = ''
          this.requestWebsocket()
        }),
      )
      .subscribe()

    this.filteredLookups$ = this.form.controls.lookup.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      startWith(''),
      map((value) => this.filterAutocompleteLookups(value)),
    )

    this.filteredProviders$ = this.form.controls.provider.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      startWith(''),
      map((value) => this.filterAutocompleteProviders(value)),
    )

    this.serviceFilterCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.filterServiceTypes())

    this.dialogRef.backdropClick().subscribe(() => {
      // Update when the overlay's backdrop has been clicked
      this.dialogRef.close(this.eligibility)
    })

    this.form.controls.insurance.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        combineLatestWith(this.lookups$),
        tap(([insurance, lookups]) => {
          const lookup = lookups.find(
            (lookup) => lookup.id === insurance?.carrierLookupId,
          )
          this.form.patchValue({
            memberId: insurance?.memberId,
            groupNumber: insurance?.groupNumber,
            lookup,
          })
          this.form.updateValueAndValidity()
          if (insurance && !lookup) {
            this.setPayerError(undefined)
          }
        }),
      )
      .subscribe()

    this.form.controls.lookup.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        tap((lookup) => {
          if (!(lookup instanceof InsuranceProviderLookup)) {
            this.payerErrorMessage = ''
            if (lookup == null) {
              this.requestConfiguration(undefined)
            }
            return
          }

          this.setPayerError(lookup.carrierId)
          this.filterServiceTypesByLookup(lookup)
          this.requestConfiguration(lookup)
        }),
      )
      .subscribe()

    this.form.controls.provider.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        filter((value) => value instanceof InsuranceProvider),
        tap((provider) => {
          this.payerErrorMessage = ''
          this.filterServiceTypesByProvider(provider)
          this.requestConfiguration(undefined) // provider.slug
        }),
      )
      .subscribe()

    if (
      this.patient &&
      !this.insurances$.getValue() &&
      this.data.insurance == null
    ) {
      this.loadPatientInsurances()
    }

    if (this.data.insurance) {
      if (this.data.insurance.carrierLookupId) {
        this.progressService.start('lookup')
        this.insuranceProviderLookupsService
          .get(this.data.insurance.carrierLookupId)
          .pipe(
            takeUntil(this.onDestroy$),
            tap((lookup) => {
              this.form.patchValue({ lookup })
            }),
            catchError((err) => {
              this.errorMessage =
                get(err, 'error.message') ||
                this.translateService.instant('dialog.eligibility.unknown-error')
              return EMPTY
            }),
            finalize(() => this.progressService.stop('lookup')),
          )
          .subscribe()
      }
    } else {
      this.insurances$
        .pipe(
          takeUntil(this.onDestroy$),
          filter(Boolean),
          combineLatestWith(this.lookups$.pipe(takeUntil(this.onDestroy$))),
          map(([insurances, lookups]) =>
            this.filterAvailableCarriers(insurances, lookups),
          ),
          tap((lookups) => this.requestConfiguration(lookups[0])),
        )
        .subscribe()
    }

    this.form.get('dateOfService').disable()

    this.facilityService.current$
      .pipe(switchMap((facility) => {
        this.facility = facility
        return this.insuranceProviderConfigurationService.configuration$
      }))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((config) => {
        if (config) {
          const mappedServiceTypes = this.mapServiceTypes(config)

          // Availity omits maxRepeats entirely for payers it does not advertise
          // as repeating, which is not the same as a limit of one, so the
          // selection is left unbounded rather than capped at a guess.
          this.maxRepeats = config.elements?.serviceType?.repeats
            ? config.elements.serviceType.maxRepeats
            : undefined

          this.selectDefaultServiceTypeCodes(mappedServiceTypes)

          this.form.patchValue({
            dateOfService: this.getDateOfService(),
          })

          if (config.elements?.asOfDate?.allowed) {
            this.form.get('dateOfService').enable()
          } else {
            this.form.patchValue({ dateOfService: moment.tz(this.tz) })
            this.form.get('dateOfService').disable()
          }
        } else {
          this.maxRepeats = undefined
          this.selectDefaultServiceTypeCodes()
          this.form.patchValue({ dateOfService: moment.tz(this.tz) })
          this.form.get('dateOfService').disable()
        }

        this.form.updateValueAndValidity()
      }, () => {
        this.form.patchValue({ dateOfService: moment.tz(this.tz) })
        this.form.get('dateOfService').disable()
      })
  }

  ngAfterViewInit(): void {
    if (
      this.data.insurance &&
      !this.data.insurance?.carrierId &&
      !this.data.insurance?.carrierLookupId
    ) {
      this.switchToProviders()
    } else if (!this.data.insurance?.carrierLookupId) {
      this.switchToLookups()
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private loadPatientInsurances() {
    this.progressService.start('insurances')
    this.crudService
      .index(PatientInsurance, { patientId: this.patient.id })
      .pipe(
        takeUntil(this.onDestroy$),
        tap((insurances) => this.insurances$.next(insurances)),
        finalize(() => this.progressService.stop('insurances')),
      )
      .subscribe()
  }

  getDateOfService(): moment.Moment {
    if (this.data.appointment?.startAt) {
      const dos = this.data.appointment?.startAt
      if (dos.isBefore(moment(), 'day')) {
        return dos
      }
    }

    return moment()
  }

  getErrorMessage(controlName) {
    const control = this.form.get(controlName)

    if (control) {
      if (control.hasError('required')) {
        return this.translateService.instant(
          'dialog.eligibility.field-required',
        )
      } else if (control.hasError('oneOf')) {
        return this.translateService.instant(
          'dialog.eligibility.not-valid-selection',
        )
      }
    }
  }

  onUseDOBChange() {
    if (this.requiresMemberId) {
      this.form.controls.useDOB.setValue(false)
      this.form.controls.useDOB.disable()
    } else {
      this.form.controls.useDOB.enable()
    }

    this.form.controls.useDOB.updateValueAndValidity()

    if (this.form.value.useDOB) {
      this.form.get('memberId').setValidators([])
      this.form.get('memberId').disable()
    } else {
      this.form.get('memberId').setValidators([Validators.required])
      this.form.get('memberId').enable()
    }

    this.form.updateValueAndValidity()
  }

  onLookupSelected(lookup: InsuranceProviderLookup) {
    this.carrierNotes = lookup.notes
    if (lookup.provider) {
      this.onProviderSelected(lookup.provider)
    }
  }

  onProviderSelected(provider: InsuranceProvider) {
    this.requiresMemberId = provider.requiresMemberId

    if (this.requiresMemberId) {
      this.onUseDOBChange()
    }
  }

  onCancelClick() {
    this.dialogRef.close(this.eligibility)
  }

  get hasPayer(): boolean {
    return Boolean(
      this.form.controls.provider.value?.id ||
      this.form.controls.lookup.value?.carrierId,
    )
  }

  private setPayerError(carrierId?: string): void {
    this.payerErrorMessage = carrierId ? '' : this.translateService.instant(NO_PAYER_CONFIGURED_ERROR)
  }

  switchToProviders(): void {
    this.showLookupSelector = false
    this.showProviderSelector = true
    this.form.patchValue({ lookup: undefined })

    const done = () => {
      this.form.controls.provider.addValidators([
        Validators.required,
        oneOfValidator(this.providers),
      ])
      this.form.controls.provider.updateValueAndValidity()
      this.form.controls.lookup.clearValidators()
      this.form.controls.lookup.updateValueAndValidity()
    }

    if (!this.providers || this.providers.length === 0) {
      this.progressService.start('providers')
      this.restService
        .getPayers()
        .pipe(
          takeUntil(this.onDestroy$),
          tap((providers) => {
            this.providers = providers
            this.fuseProviders = new Fuse(this.providers, {
              keys: ['viewValue'],
              shouldSort: true,
            })
            done()
          }),
          finalize(() => this.progressService.stop('providers')),
        )
        .subscribe()
    } else {
      done()
    }
  }

  switchToLookups(): void {
    this.showLookupSelector = true
    this.showProviderSelector = false
    this.form.patchValue({ provider: undefined })

    const done = () => {
      this.form.controls.lookup.addValidators([
        Validators.required,
        oneOfValidator(this.lookups),
      ])
      this.form.controls.lookup.updateValueAndValidity()
      this.form.controls.provider.clearValidators()
      this.form.controls.provider.updateValueAndValidity()
    }

    if (!this.lookups || this.lookups.length === 0) {
      this.lookups$
        .pipe(
          tap(() => {
            if (this.data.insurance) {
              const lookup = this.lookups.find(
                (c) => c.id === this.data.insurance?.carrierLookupId,
              )
              this.form.patchValue({
                lookup,
              })
            }

            done()
          }),
        )
        .subscribe()
    } else {
      done()
    }
  }

  private getRequestObject(): PullEligibilityRequest {
    const value = this.form.value

    let serviceTypeCodes: string[] | null = null

    if (Array.isArray(value.serviceTypeCodes)) {
      serviceTypeCodes = value.serviceTypeCodes
    } else if (typeof value.serviceTypeCodes === 'string') {
      serviceTypeCodes = [value.serviceTypeCodes]
    }

    return {
      patientId: this.patient?.id,
      patientInsuranceId: value.insurance?.id,
      lookupId: value.lookup?.id ?? value.insurance?.carrierLookupId,
      providerId: value.provider?.id ?? value.lookup?.carrierId,
      insuranceType: this.data.insuranceType,
      appointmentId: this.data.appointment?.id,
      facilityId: this.form.controls.facilityId.getRawValue(),
      memberId: value.memberId,
      groupNumber: value.groupNumber,
      patientName: value.patientName,
      dob: value.dob?.format('YYYY-MM-DD'),
      sex: value.sex,
      dateOfService: value.dateOfService?.format('YYYY-MM-DD'),
      serviceTypeCodes: serviceTypeCodes,
    }
  }

  private requestWebsocket() {
    const body = this.getRequestObject()

    this.ws.messageQueue.next({
      action: WebsocketAction.ELIGIBILITY_INQUIRY_CONSOLE,
      ...body,
    })
  }

  displayAutocompleteLabel(lookup: SelectOption | undefined): string {
    return lookup?.viewValue
  }

  private filterServiceTypesByLookup(lookup: InsuranceProviderLookup): void {
    if (lookup.provider) {
      this.filterServiceTypesByProvider(lookup.provider)
    }
  }

  private filterServiceTypesByProvider(provider: InsuranceProvider) {
    if (isEmpty(provider.serviceTypesUnsupported)) {
      this.filteredServiceTypes.next(this.serviceTypes.slice())
    } else {
      const serviceTypesFiltered = this.serviceTypes.filter(
        (s) => !provider.serviceTypesUnsupported?.includes(s.code),
      )
      this.filteredServiceTypes.next(serviceTypesFiltered.slice())
      this.filterServiceTypeSelection(provider)
    }
  }

  private filterServiceTypeSelection(provider: InsuranceProvider) {
    // Check if the selected serviceTypeCode is supported by the carrier
    const serviceTypeCodes = new Set(this.form.controls.serviceTypeCodes.value)
    const unsupportedCodes = provider.serviceTypesUnsupported ?? []
    for (const unsupportedCode of unsupportedCodes) {
      serviceTypeCodes.delete(unsupportedCode)
    }
    this.form.controls.serviceTypeCodes.setValue([...serviceTypeCodes])
  }

  private filterAutocompleteLookups(
    val: string | InsuranceProviderLookup,
  ): InsuranceProviderLookup[] {
    if (val && typeof val === 'string') {
      return this.fuseLookups.search(val).map((result) => result.item)
    } else if (
      (!val && this.popularInsurances?.length > 0) ||
      (val instanceof InsuranceProviderLookup &&
        this.popularInsurances?.includes(val))
    ) {
      // Show popular insurances on empty input search
      return this.popularInsurances
    } else {
      return this.lookups
    }
  }

  filterAutocompleteProviders(
    val: string | InsuranceProvider,
  ): InsuranceProvider[] {
    if (val && typeof val === 'string') {
      return this.fuseProviders.search(val).map((result) => result.item)
    } else {
      return this.providers
    }
  }

  private filterServiceTypes() {
    if (!this.serviceTypes) {
      return
    }
    const search = this.serviceFilterCtrl.value
    if (!search) {
      this.filteredServiceTypes.next(this.serviceTypes.slice())
    } else {
      this.filteredServiceTypes.next(
        this.fuseServiceTypes.search(search).map((result) => result.item),
      )
    }
  }

  /**
   * Filters all of the clinic's lookups for only those linked to one of the patient's insurances
   */
  private filterAvailableCarriers(
    insurances: PatientInsurance[],
    lookups: InsuranceProviderLookup[],
  ): InsuranceProviderLookup[] {
    return insurances
      .map(({ carrierLookupId }) =>
        lookups.find(({ id }) => id === carrierLookupId),
      )
      .filter(Boolean)
      .filter(({ provider }) =>
        provider?.supportedTransactions?.includes(
          INSURANCE_PROVIDER_TRANSACTION_TYPE.ELIGIBILITY_INQUIRY,
        ),
      )
  }

  private requestConfiguration(providerLookup?: InsuranceProviderLookup) {
    if (providerLookup?.provider?.slug) {
      this.insuranceProviderConfigurationService.configurationParams$.next({
        providerLookupId: providerLookup?.id,
        payerId: providerLookup?.provider?.slug,
        type: InsuranceProviderConfiguration.TYPE.COVERAGES,
      })
    } else {
      this.maxRepeats = undefined
      this.selectDefaultServiceTypeCodes()
      this.form.updateValueAndValidity()
    }
  }

  /**
   * Pre-selects the codes the appointment calls for, falling back to the
   * clinic's defaults when the appointment asks for nothing the payer lists.
   * Never leaves the selection empty, since the field is required.
   */
  private selectDefaultServiceTypeCodes(payerServiceTypes?: ServiceType[]): void {
    const appointmentCodes = uniq(
      flatten(
        (this.data.appointment?.products ?? []).map((product) => product.serviceCodes ?? []),
      ),
    )
    const defaultCodes = this.defaultServiceTypeCodes

    let serviceTypeCodes = filterSupportedCodes(appointmentCodes, payerServiceTypes)

    if (!serviceTypeCodes.length) {
      serviceTypeCodes = filterSupportedCodes(defaultCodes, payerServiceTypes)
    }

    if (!serviceTypeCodes.length) {
      serviceTypeCodes = [defaultCodes[0] ?? FALLBACK_SERVICE_TYPE_CODE]
    }

    this.form.patchValue({ serviceTypeCodes })
  }

  private mapServiceTypes(
    responseData: InsuranceProviderConfigurationModel,
  ): ServiceType[] {
    let serviceTypes: ServiceType[]

    if (responseData?.elements?.serviceType?.values) {
      serviceTypes = responseData.elements.serviceType.values.map(
        (value: any) => ({
          code: value.code,
          name: value.value,
        }),
      )
      this.filteredServiceTypes.next(serviceTypes)
      return serviceTypes
    } else {
      this.filterServiceTypesByProvider(this.form.controls.provider.value)
    }
  }

  private updateServiceTypeCodesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!Array.isArray(control.value)) {
        return null
      }

      const serviceTypeCodes: string[] = control.value
      const isValidRepeatCount =
        this.maxRepeats != null && serviceTypeCodes.length > this.maxRepeats

      if (isValidRepeatCount) {
        return {
          maxLimitReached: {
            message: `You have reached the maximum limit of ${this.maxRepeats} for Service Types`,
          },
        }
      }

      return null
    }
  }
}
